import React, { createContext, Dispatch, SetStateAction, useContext, useEffect, useState } from 'react'
import { Location } from 'react-router-dom'

import { ProjectStatus } from '@makyoapp/makyo-sdk/models/Projects'

import { queryClient, queryKeys } from '@/lib/react-query'
import useAuth from '@/hooks/useAuth'
import { EVENT_KEYS } from '@/shared/custom-event'

export interface AppProjectState {
  id: string
  title: string
  url: string
  icon: string
  color: string
  orgId: string
  loading: boolean
  status: ProjectStatus
}

interface Toolbar {
  show: (location: Location) => boolean
  component: React.ReactNode
}

/**
 * Global App State
 */
interface AppState {
  /**
   * project identity to show in toolbar
   */
  project?: AppProjectState
  setProject: Dispatch<SetStateAction<AppProjectState | undefined>>
  version: string
  setVersion: Dispatch<SetStateAction<string>>
  /**
   * show slide menu state for mobile navigation
   */
  showSlideMenu: boolean
  /**
   * toggle slide menu
   */
  toggleSlideMenu: () => void

  projectAction: React.ReactNode | null
  setProjectAction: Dispatch<SetStateAction<React.ReactNode | null>>
  /**
   * toolbar
   */
  toolbar: Toolbar[]
  setToolbar: Dispatch<SetStateAction<Toolbar[]>>
}

const AppContext = createContext<AppState | undefined>(undefined)

/**
 * use app context hook
 */
export function useAppContext() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppContext must be used within a AppProvider')
  }
  return context
}

interface AppContextProviderProps {
  children?: React.ReactNode
}

const DefaultProjectLoading: AppProjectState = {
  id: '',
  title: '',
  url: '',
  icon: '',
  color: '',
  orgId: '',
  status: ProjectStatus.Draft,
  loading: true,
}

/**
 * Global AppProvider
 */
export const AppProvider = ({ children }: AppContextProviderProps) => {
  // access auth
  const { userData } = useAuth()
  const userId = userData?.id

  // state
  const [slide, setSlide] = useState(false)
  const [project, setProject] = useState<AppProjectState | undefined>(undefined)
  const [version, setVersion] = useState('')
  const [projectAction, setProjectAction] = useState<React.ReactNode | null>(null)
  const [toolbar, setToolbar] = useState<Toolbar[]>([])

  const toggleSlideMenu = () => {
    setSlide(!slide)
  }

  useEffect(() => {
    const handleAppBuilderLoading = () => {
      setProject(DefaultProjectLoading)
    }

    const handleAppBuilderLoaded = (e: Event) => {
      const detail = (e as CustomEvent<AppProjectState>).detail
      setProject(detail)
      queryClient.invalidateQueries({ queryKey: [queryKeys.recentApps, userId] })
      // update local recent apps cache
      try {
        if (!userId) return
        const key = `recentApps:${userId}`
        const raw = localStorage.getItem(key)
        const parsed = raw ? JSON.parse(raw) : []
        const currentList: any[] = Array.isArray(parsed) ? parsed : []
        const filtered = currentList.filter((a) => a && a.id !== detail.id)
        const newItem = {
          id: detail.id,
          title: detail.title,
          icon: detail.icon,
          iconBgColor: detail.color,
          org: { id: detail.orgId },
          accessedAt: new Date().toISOString(),
        }
        const updated = [newItem, ...filtered].slice(0, 5)
        localStorage.setItem(key, JSON.stringify(updated))
        window.dispatchEvent(new CustomEvent(EVENT_KEYS.RECENT_APPS_UPDATED, { detail: updated }))
      } catch (_) {
        // ignore localStorage errors
      }
    }

    const abortController = new AbortController()
    const signal = abortController.signal

    window.addEventListener(EVENT_KEYS.APP_BUILDER_LOADING, handleAppBuilderLoading, { signal })
    window.addEventListener(EVENT_KEYS.APP_BUILDER_LOADED, handleAppBuilderLoaded, { signal })

    return () => {
      abortController.abort()
    }
  }, [userId])

  return (
    <>
      <AppContext.Provider
        value={{
          showSlideMenu: slide,
          project,
          toggleSlideMenu,
          setProject,
          toolbar,
          setToolbar,
          projectAction,
          setProjectAction,
          version,
          setVersion,
        }}
      >
        {children}
      </AppContext.Provider>
    </>
  )
}
